export const DEBUG_TIMELINE_VERSION = 1;
export const DEFAULT_DEBUG_TIMELINE_MAX_FRAMES = 600;

export type DebugTimelineVec3 = {
  x: number;
  y: number;
  z: number;
};

export type DebugTimelineFrame = {
  t: number;
  phase: string;
  map: string;
  tick: number;
  pos: DebugTimelineVec3;
  vel: DebugTimelineVec3;
  hp: number;
  events: number;
  effects: number;
  lastEvent?: string;
  bot?: string;
};

export type DebugTimelineInputFrame = {
  timestampMs: number;
  phase: string | null | undefined;
  mapId: string | null | undefined;
  serverTick: number;
  local: {
    position?: Partial<DebugTimelineVec3> | null;
    velocity?: Partial<DebugTimelineVec3> | null;
    health?: number | null;
  };
  eventCount: number;
  effectCount: number;
  lastEvent?: string | null;
  botIntent?: string | null;
};

export type DebugTimelineSummary = {
  version: 1;
  frameCount: number;
  maxFrames: number;
  firstTimestampMs: number | null;
  latestTimestampMs: number | null;
  latestPhase: string | null;
  latestMapId: string | null;
  latestServerTick: number | null;
  latestEvent: string | null;
  averageSpeed: number;
  peakSpeed: number;
  minHealth: number | null;
  totalEvents: number;
  totalEffects: number;
};

export type DebugTimelineImportResult = {
  ok: boolean;
  imported: number;
  dropped: number;
  error?: string;
};

export type DebugTimeline = {
  recordFrame: (frame: DebugTimelineInputFrame) => void;
  exportJson: () => string;
  importJson: (json: string) => DebugTimelineImportResult;
  clear: () => void;
  getFrames: () => DebugTimelineFrame[];
  getLatestFrame: () => DebugTimelineFrame | null;
  getSummary: () => DebugTimelineSummary;
};

type DebugTimelineOptions = {
  maxFrames?: number;
};

function finiteNumber(value: unknown, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function wholeNumber(value: unknown, fallback: number) {
  return Math.max(0, Math.trunc(finiteNumber(value, fallback)));
}

function cleanString(value: unknown, fallback: string, maxLength = 80) {
  if (typeof value !== 'string') {
    return fallback;
  }
  const trimmed = value.trim();
  return (trimmed.length > 0 ? trimmed : fallback).slice(0, maxLength);
}

function optionalString(value: unknown, maxLength = 120) {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed.slice(0, maxLength) : undefined;
}

function vec3(value: unknown): DebugTimelineVec3 {
  const input = value && typeof value === 'object' ? value as Partial<DebugTimelineVec3> : {};
  return {
    x: finiteNumber(input.x, 0),
    y: finiteNumber(input.y, 0),
    z: finiteNumber(input.z, 0),
  };
}

function sanitizeExportFrame(value: unknown): DebugTimelineFrame | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const input = value as Record<string, unknown>;
  const frame: DebugTimelineFrame = {
    t: wholeNumber(input.t, 0),
    phase: cleanString(input.phase, 'unknown', 40),
    map: cleanString(input.map, 'unknown', 40),
    tick: wholeNumber(input.tick, 0),
    pos: vec3(input.pos),
    vel: vec3(input.vel),
    hp: Math.max(0, finiteNumber(input.hp, 0)),
    events: wholeNumber(input.events, 0),
    effects: wholeNumber(input.effects, 0),
  };
  const lastEvent = optionalString(input.lastEvent);
  if (lastEvent) frame.lastEvent = lastEvent;
  const bot = optionalString(input.bot);
  if (bot) frame.bot = bot;
  return frame;
}

function sanitizeInputFrame(input: DebugTimelineInputFrame): DebugTimelineFrame {
  const frame: DebugTimelineFrame = {
    t: wholeNumber(input.timestampMs, 0),
    phase: cleanString(input.phase, 'unknown', 40),
    map: cleanString(input.mapId, 'unknown', 40),
    tick: wholeNumber(input.serverTick, 0),
    pos: vec3(input.local.position),
    vel: vec3(input.local.velocity),
    hp: Math.max(0, finiteNumber(input.local.health, 0)),
    events: wholeNumber(input.eventCount, 0),
    effects: wholeNumber(input.effectCount, 0),
  };
  const lastEvent = optionalString(input.lastEvent);
  if (lastEvent) frame.lastEvent = lastEvent;
  const bot = optionalString(input.botIntent, 120);
  if (bot) frame.bot = bot;
  return frame;
}

function speed(frame: DebugTimelineFrame) {
  return Math.sqrt(frame.vel.x * frame.vel.x + frame.vel.y * frame.vel.y + frame.vel.z * frame.vel.z);
}

export function createDebugTimeline(options: DebugTimelineOptions = {}): DebugTimeline {
  const maxFrames = Math.max(1, Math.trunc(options.maxFrames ?? DEFAULT_DEBUG_TIMELINE_MAX_FRAMES));
  let frames: DebugTimelineFrame[] = [];

  const pushFrame = (frame: DebugTimelineFrame) => {
    frames.push(frame);
    if (frames.length > maxFrames) {
      frames = frames.slice(frames.length - maxFrames);
    }
  };

  return {
    recordFrame(frame) {
      pushFrame(sanitizeInputFrame(frame));
    },
    exportJson() {
      return JSON.stringify({ version: DEBUG_TIMELINE_VERSION, frames });
    },
    importJson(json) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(json);
      } catch {
        return { ok: false, imported: 0, dropped: 0, error: 'invalid-json' };
      }

      if (!parsed || typeof parsed !== 'object') {
        return { ok: false, imported: 0, dropped: 0, error: 'invalid-root' };
      }

      const input = parsed as { frames?: unknown; version?: unknown };
      if (input.version !== DEBUG_TIMELINE_VERSION || !Array.isArray(input.frames)) {
        return { ok: false, imported: 0, dropped: 0, error: 'invalid-schema' };
      }

      let dropped = 0;
      frames = [];
      for (const rawFrame of input.frames) {
        const frame = sanitizeExportFrame(rawFrame);
        if (!frame) {
          dropped += 1;
          continue;
        }
        pushFrame(frame);
      }

      return { ok: true, imported: frames.length, dropped };
    },
    clear() {
      frames = [];
    },
    getFrames() {
      return frames.slice();
    },
    getLatestFrame() {
      return frames[frames.length - 1] ?? null;
    },
    getSummary() {
      const latest = frames[frames.length - 1] ?? null;
      const totalSpeed = frames.reduce((sum, frame) => sum + speed(frame), 0);
      const peakSpeed = frames.reduce((max, frame) => Math.max(max, speed(frame)), 0);
      const minHealth = frames.length > 0 ? frames.reduce((min, frame) => Math.min(min, frame.hp), Number.POSITIVE_INFINITY) : null;
      return {
        version: DEBUG_TIMELINE_VERSION,
        frameCount: frames.length,
        maxFrames,
        firstTimestampMs: frames[0]?.t ?? null,
        latestTimestampMs: latest?.t ?? null,
        latestPhase: latest?.phase ?? null,
        latestMapId: latest?.map ?? null,
        latestServerTick: latest?.tick ?? null,
        latestEvent: latest?.lastEvent ?? null,
        averageSpeed: frames.length > 0 ? totalSpeed / frames.length : 0,
        peakSpeed,
        minHealth,
        totalEvents: frames.reduce((sum, frame) => sum + frame.events, 0),
        totalEffects: frames.reduce((sum, frame) => sum + frame.effects, 0),
      };
    },
  };
}

export const debugTimeline = createDebugTimeline();
